import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useSuccessAlert, useErrorAlert } from './AlertSystem';
import './AuthPage.css';

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  acceptTerms?: string;
  general?: string;
}

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const showSuccess = useSuccessAlert();
  const showError = useErrorAlert();
  
  const [formData, setFormData] = useState<RegisterFormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password strength calculation
  const getPasswordStrength = (password: string): PasswordStrength => {
    let score = 0;
    
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    const strengths = [
      { score: 0, label: 'Very Weak', color: '#ef4444' },
      { score: 1, label: 'Weak', color: '#f97316' },
      { score: 2, label: 'Fair', color: '#eab308' },
      { score: 3, label: 'Good', color: '#22c55e' },
      { score: 4, label: 'Strong', color: '#16a34a' },
      { score: 5, label: 'Very Strong', color: '#15803d' }
    ];

    return strengths[score];
  };

  const passwordStrength = getPasswordStrength(formData.password);

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Full name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (passwordStrength.score < 3) {
      newErrors.password = 'Password is too weak. Please choose a stronger password';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Terms acceptance validation
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      // Use AuthContext register method
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password
      });
      
      // Show success message
      showSuccess('Business account created successfully!', 'Welcome to Nuroveli Enterprise');
      
      // Get intended destination from location state or default to dashboard
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
      
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Registration failed. Please try again.';
      showError(errorMessage, 'Registration Failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-background" />
      
      <div className="container">
        <div className="auth-container">
          
          {/* Auth Header */}
          <div className="auth-header">
            <div className="auth-logo">
              <div className="auth-logo-icon">
                <i className="ri-shield-keyhole-line"></i>
              </div>
              <span className="auth-logo-text">Nuroveli</span>
            </div>

            <h1 className="auth-title">Start Your Business Trial</h1>
            <p className="auth-subtitle">
              Enterprise-grade security solutions for forward-thinking businesses
            </p>
            
          </div>

          {/* Registration Form */}
          <form className="auth-form" onSubmit={handleSubmit}>
            
            {/* General Error */}
            {errors.general && (
              <div className="auth-error">
                <i className="ri-error-warning-line"></i>
                <span>{errors.general}</span>
              </div>
            )}

            {/* Name Field */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="name">
                Business Contact Name
              </label>
              <div className="auth-input-wrapper">
                <div className="auth-input-icon">
                  <i className="ri-user-line"></i>
                </div>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className={`auth-input ${errors.name ? 'auth-input-error' : ''}`}
                  placeholder="Enter contact person's name"
                  value={formData.name}
                  onChange={handleInputChange}
                  autoComplete="name"
                />
              </div>
              {errors.name && (
                <span className="auth-field-error">{errors.name}</span>
              )}
            </div>

            {/* Email Field */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="email">
                Business Email Address
              </label>
              <div className="auth-input-wrapper">
                <div className="auth-input-icon">
                  <i className="ri-mail-line"></i>
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`auth-input ${errors.email ? 'auth-input-error' : ''}`}
                  placeholder="Enter your business email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <span className="auth-field-error">{errors.email}</span>
              )}
            </div>

            {/* Password Field */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="password">
                Secure Account Password
              </label>
              <div className="auth-input-wrapper">
                <div className="auth-input-icon">
                  <i className="ri-lock-line"></i>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  className={`auth-input ${errors.password ? 'auth-input-error' : ''}`}
                  placeholder="Create a secure password"
                  value={formData.password}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {formData.password && (
                <div className="auth-password-strength">
                  <div className="auth-strength-bar">
                    <div 
                      className="auth-strength-fill"
                      style={{ 
                        width: `${(passwordStrength.score / 5) * 100}%`,
                        backgroundColor: passwordStrength.color
                      }}
                    />
                  </div>
                  <span 
                    className="auth-strength-label"
                    style={{ color: passwordStrength.color }}
                  >
                    {passwordStrength.label}
                  </span>
                </div>
              )}
              
              {errors.password && (
                <span className="auth-field-error">{errors.password}</span>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="confirmPassword">
                Confirm Password
              </label>
              <div className="auth-input-wrapper">
                <div className="auth-input-icon">
                  <i className="ri-lock-line"></i>
                </div>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  className={`auth-input ${errors.confirmPassword ? 'auth-input-error' : ''}`}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="auth-field-error">{errors.confirmPassword}</span>
              )}
            </div>

            {/* Terms Checkbox */}
            <div className="auth-field">
              <label className="auth-checkbox">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleInputChange}
                />
                <span className="auth-checkbox-checkmark">
                  <i className="ri-check-line"></i>
                </span>
                <span className="auth-checkbox-text">
                  I agree to the 
                  <Link to="/terms-of-service" className="auth-link" target="_blank">
                    Business Terms of Service
                  </Link> and 
                  <Link to="/privacy-policy" className="auth-link" target="_blank">
                    Enterprise Privacy Policy
                  </Link>
                </span>
              </label>
              {errors.acceptTerms && (
                <span className="auth-field-error">{errors.acceptTerms}</span>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="auth-btn auth-btn-primary"
              disabled={isLoading}
            >
              <span className="auth-btn-text">
                {isLoading ? 'Setting Up Your Business Account...' : 'Start Enterprise Trial'}
              </span>
              <div className="auth-btn-icon">
                <i className={isLoading ? 'ri-loader-4-line auth-spinner' : 'ri-arrow-right-line'}></i>
              </div>
              <div className="auth-btn-glow"></div>
            </button>

          </form>

          {/* Auth Footer */}
          <div className="auth-footer">
            <p className="auth-footer-text">
              Already have a business account? 
              <Link to="/login" className="auth-footer-link">
                Sign in here
              </Link>
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
